/**
 * Servicio de Feedback - Haptico y Sonoro
 *
 * Proporciona retroalimentacion sensorial al completar acciones.
 * Optimizado para usuarios neurodivergentes:
 * - Feedback inmediato (importante para ADHD)
 * - Configurable (respeta preferencias sensoriales)
 * - No intrusivo
 */

import { useUIStore } from '@/store';

// === TIPOS ===

type FeedbackType = 'success' | 'error' | 'warning' | 'tap' | 'complete';

interface FeedbackOptions {
  type: FeedbackType;
  intensity?: 'light' | 'medium' | 'strong';
}

// === PATRONES DE VIBRACION (en ms) ===

const VIBRATION_PATTERNS: Record<FeedbackType, number[]> = {
  tap: [10],                    // Toque ligero
  success: [50],                // Confirmacion corta
  complete: [50, 50, 100],      // Patron de celebracion
  warning: [100, 50, 100],      // Alerta
  error: [200, 100, 200],       // Error enfatico
};

// === FRECUENCIAS DE SONIDO (Hz) ===

const SOUND_FREQUENCIES: Record<FeedbackType, { freq: number; duration: number }[]> = {
  tap: [{ freq: 800, duration: 50 }],
  success: [{ freq: 880, duration: 100 }],
  complete: [
    { freq: 523, duration: 100 },  // C5
    { freq: 659, duration: 100 },  // E5
    { freq: 784, duration: 150 },  // G5
  ],
  warning: [{ freq: 440, duration: 200 }],
  error: [{ freq: 200, duration: 300 }],
};

// === AUDIO CONTEXT (lazy init) ===

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;

  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch {
      console.warn('Web Audio API no disponible');
      return null;
    }
  }

  // Reanudar si esta suspendido (requiere interaccion del usuario)
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {
      // Ignorar error si no se puede reanudar
    });
  }

  return audioContext;
}

// === FUNCIONES PRINCIPALES ===

/**
 * Genera vibracion haptica
 */
function vibrate(pattern: number[]): void {
  if (typeof navigator === 'undefined') return;
  if (!('vibrate' in navigator)) return;

  try {
    navigator.vibrate(pattern);
  } catch {
    // Ignorar errores de vibracion
  }
}

/**
 * Genera sonido con Web Audio API
 */
function playTone(frequencies: { freq: number; duration: number }[], volume = 0.3): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  let startTime = ctx.currentTime;

  frequencies.forEach(({ freq, duration }) => {
    try {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.value = freq;
      oscillator.type = 'sine';

      // Envelope suave para evitar clicks
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration / 1000);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration / 1000 + 0.01);

      startTime += duration / 1000;
    } catch {
      // Ignorar errores de audio
    }
  });
}

/**
 * Proporciona feedback haptico y/o sonoro segun preferencias
 */
export function provideFeedback(options: FeedbackOptions): void {
  const { type, intensity = 'medium' } = options;
  const preferences = useUIStore.getState().preferences;

  // Ajustar volumen segun intensidad
  const volumeMap = { light: 0.15, medium: 0.3, strong: 0.5 };
  const volume = volumeMap[intensity];

  // Feedback haptico
  if (preferences.hapticEnabled) {
    const pattern = VIBRATION_PATTERNS[type];
    if (pattern) {
      vibrate(pattern);
    }
  }

  // Feedback sonoro
  if (preferences.soundEnabled) {
    const frequencies = SOUND_FREQUENCIES[type];
    if (frequencies) {
      playTone(frequencies, volume);
    }
  }
}

// === SHORTCUTS PARA USO COMUN ===

/**
 * Feedback al tocar/interactuar
 */
export function feedbackTap(): void {
  provideFeedback({ type: 'tap', intensity: 'light' });
}

/**
 * Feedback al completar una subtarea
 */
export function feedbackSuccess(): void {
  provideFeedback({ type: 'success', intensity: 'medium' });
}

/**
 * Feedback al completar un protocolo completo
 */
export function feedbackComplete(): void {
  provideFeedback({ type: 'complete', intensity: 'strong' });
}

/**
 * Feedback de advertencia
 */
export function feedbackWarning(): void {
  provideFeedback({ type: 'warning', intensity: 'medium' });
}

/**
 * Feedback de error
 */
export function feedbackError(): void {
  provideFeedback({ type: 'error', intensity: 'strong' });
}

// === HOOK PARA COMPONENTES ===

/**
 * Hook que devuelve funciones de feedback
 * Util para componentes que necesitan feedback frecuente
 */
export function useFeedback() {
  return {
    tap: feedbackTap,
    success: feedbackSuccess,
    complete: feedbackComplete,
    warning: feedbackWarning,
    error: feedbackError,
    custom: provideFeedback,
  };
}
